import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import {
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";

import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import {
  createProject,
  getCloudState,
  getCloudToken,
  getProject,
  saveProjectData,
  setActiveProject,
  setSaveStatus,
} from "./api";

import type { ProjectMeta } from "./api";

const AUTOSAVE_DELAY = 2000;

type ScenePayload = {
  elements: unknown[];
  appState: Record<string, unknown>;
  files: Record<string, unknown>;
};

function buildPayload(
  elements: readonly OrderedExcalidrawElement[],
  appState: AppState,
  files: BinaryFiles,
): ScenePayload {
  return {
    elements: [...elements],
    appState: {
      viewBackgroundColor: appState.viewBackgroundColor,
      scrollX: appState.scrollX,
      scrollY: appState.scrollY,
      zoom: appState.zoom,
    },
    files: files as Record<string, unknown>,
  };
}

class CloudSyncManager {
  private api: ExcalidrawImperativeAPI | null = null;
  private lastSavedJson = new Map<string, string>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private saving = false;
  private pendingAfterSave = false;
  private initialized = false;

  setAPI(api: ExcalidrawImperativeAPI) {
    this.api = api;
  }

  /** Restaura o projeto ativo (se houver) após a carga inicial da cena. */
  async init() {
    if (this.initialized || !this.api) {
      return;
    }
    this.initialized = true;

    // não interferir com links externos/colaboração
    if (/^#json=|^#room=/.test(window.location.hash)) {
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("id")) {
      return;
    }

    const { activeProject } = getCloudState();
    if (!getCloudToken() || !activeProject) {
      return;
    }

    try {
      const { project } = await getProject(activeProject.id);
      setActiveProject({
        id: project.id,
        name: project.name,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
      });
      this.applyToCanvas(project.data, activeProject.id);
    } catch (error) {
      console.warn("Nao foi possivel restaurar o projeto da nuvem:", error);
    }
  }

  private applyToCanvas(data: any, projectId: string) {
    const api = this.api;
    if (!api) {
      return;
    }
    const files = (Object.values(data.files || {}) as BinaryFileData[]).filter(
      Boolean,
    );
    if (files.length) {
      api.addFiles(files);
    }
    const restoredElements = restoreElements(
      (data.elements || []) as any,
      null,
      { repairBindings: true },
    );
    const restoredAppState = restoreAppState(
      (data.appState || {}) as any,
      null,
    );
    api.updateScene({
      elements: restoredElements,
      appState: restoredAppState,
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });

    // registra estado atual como já salvo para não disparar autosave imediato
    try {
      const payload = buildPayload(
        api.getSceneElementsIncludingDeleted() as any,
        api.getAppState(),
        api.getFiles(),
      );
      this.lastSavedJson.set(projectId, JSON.stringify(payload));
    } catch {
      // ignora
    }
  }

  /**
   * Chamado a cada mudança na cena. Agenda autosave se logado e com projeto ativo.
   */
  notifyChange(
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) {
    const { user, activeProject } = getCloudState();
    if (!user || !activeProject) {
      return;
    }

    let json: string;
    try {
      json = JSON.stringify(buildPayload(elements, appState, files));
    } catch {
      return;
    }

    const saved = this.lastSavedJson.get(activeProject.id);
    if (saved === json) {
      return;
    }

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }
    this.saveTimer = setTimeout(() => {
      void this.flush(json!);
    }, AUTOSAVE_DELAY);
  }

  private async flush(json?: string) {
    const { activeProject } = getCloudState();
    const api = this.api;
    if (!activeProject || !api) {
      return;
    }
    if (this.saving) {
      this.pendingAfterSave = true;
      return;
    }

    let payloadJson = json;
    if (!payloadJson) {
      try {
        payloadJson = JSON.stringify(
          buildPayload(
            api.getSceneElementsIncludingDeleted() as any,
            api.getAppState(),
            api.getFiles(),
          ),
        );
      } catch {
        return;
      }
    }
    if (this.lastSavedJson.get(activeProject.id) === payloadJson) {
      return;
    }

    this.saving = true;
    setSaveStatus("saving");
    try {
      const payload = JSON.parse(payloadJson);
      const res = await saveProjectData(activeProject.id, payload);
      this.lastSavedJson.set(activeProject.id, payloadJson);
      setSaveStatus("saved");
      setActiveProject({ ...activeProject, updatedAt: res.updatedAt });
    } catch (error) {
      console.warn("Falha ao salvar projeto na nuvem:", error);
      setSaveStatus("error");
    } finally {
      this.saving = false;
      if (this.pendingAfterSave) {
        this.pendingAfterSave = false;
        this.saveTimer = setTimeout(() => {
          void this.flush();
        }, 500);
      }
    }
  }

  /** Salva imediatamente (usado antes de trocar de projeto / abrir painel). */
  flushNow() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    void this.flush();
  }

  /** Cria um projeto novo em branco e torna-o ativo. */
  async newProject(name: string) {
    if (!this.api) {
      return;
    }
    const meta = await createProject(name);
    setActiveProject(meta);
    this.lastSavedJson.delete(meta.id);

    this.api.updateScene({
      elements: [],
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
    this.api.updateScene({
      appState: { viewBackgroundColor: "#ffffff" },
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    this.lastSavedJson.set(
      meta.id,
      JSON.stringify(await this.currentPayload()),
    );
    return meta;
  }

  private async currentPayload(): Promise<ScenePayload> {
    const api = this.api!;
    return buildPayload(
      api.getSceneElementsIncludingDeleted() as any,
      api.getAppState(),
      api.getFiles(),
    );
  }

  /** Salva a cena atual como um novo projeto na nuvem. */
  async saveAsNew(name: string): Promise<ProjectMeta | undefined> {
    const api = this.api;
    if (!api) {
      return;
    }
    const meta = await createProject(name);
    const payload = await this.currentPayload();
    await saveProjectData(meta.id, payload);
    this.lastSavedJson.set(meta.id, JSON.stringify(payload));
    setActiveProject({ ...meta, updatedAt: new Date().toISOString() });
    setSaveStatus("saved");
    return meta;
  }

  /** Abre um projeto da lista e aplica no canvas. */
  async openProject(meta: ProjectMeta) {
    this.flushNow();
    const { project } = await getProject(meta.id);
    setActiveProject({
      id: project.id,
      name: project.name,
      createdAt: project.createdAt,
      updatedAt: project.updatedAt,
    });
    this.applyToCanvas(project.data, project.id);
    setSaveStatus("idle");
  }
}

export const cloudSync = new CloudSyncManager();
